import { DateTime } from "luxon";
import { useCallback, useMemo, useRef, useState } from "react";
import { type AnalyticValues, useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { isOtherReasonLabel } from "../../../../utils/callIns";
import { ChangesSource, type NurseInShift } from "../../../../utils/models";
import { useDeleteRecurringOrSingleState } from "../../../NormalizedNurseMenu/DeleteRecurringOrSingleShift/useDeleteRecurringOrSingleState";
import { REMOVE_NURSE_DAYS_THRESHOLD } from "../../../ShiftData/ShiftDataNurseRemoval";
import { useCallInReasonOptions } from "../../../team/CallInReasons/callInReasonsHooks";
import { canConfirmRemoval, needsFarOutConfirm, removalDaysOut } from "./gapsRemovalGates";
import type { GapsGroupTotals, GapsRemovalOutcome, GapsRemovalSource, GapsSheetGroupLabel } from "./gapsSheetTypes";

/** The desktop removal dialog's own state (`useDeleteRecurringOrSingleState`), which the removal step lays out. */
export type GapsRemovalState = ReturnType<typeof useDeleteRecurringOrSingleState>;

/** The department's call-in reasons as the picker needs them: the options, and whether the read settled. */
export type GapsRemovalReasons = Pick<ReturnType<typeof useCallInReasonOptions>, "options" | "isLoading" | "isError" | "isSuccess">;

type Params = {
	nurseInShift: NurseInShift;
	name: string;
	group: GapsSheetGroupLabel;
	totalsBefore: GapsGroupTotals | null;
	source: GapsRemovalSource;
	onBack: () => void;
	onRemoved: (outcome: GapsRemovalOutcome) => void;
};

/** What the write is about to do, captured at confirm. */
type PendingRemoval = { callOffFired: boolean; reason: string };

/**
 * How a removal ends, reported: the wrapped hook calls `onFinished` after its own await, so what the write was about to
 * do is captured at confirm (`pendingRef`) and the removal is reported as it was confirmed, not as the rebuilt day reads
 * afterwards.
 */
const useRemovalOutcome = ({ nurseInShift, name, group, totalsBefore, source, onRemoved }: Omit<Params, "onBack">) => {
	const { track, trackError } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const pendingRef = useRef<PendingRemoval | null>(null);
	const analyticsBase = useMemo<AnalyticValues>(
		() => ({ department_id: chosenDepartment.department_id, shift_id: nurseInShift.id, nurse_id: nurseInShift.nurseId, source }),
		[chosenDepartment.department_id, nurseInShift.id, nurseInShift.nurseId, source],
	);

	const onFinished = useCallback(
		(deleted: boolean, error?: unknown): void => {
			const pending = pendingRef.current;
			pendingRef.current = null;
			if (pending === null) return;
			if (!deleted) {
				// The hook has already shown the failure in the error snackbar; the step stays so the user can retry.
				trackError("gaps_removal_error", {
					...analyticsBase,
					reason: pending.reason,
					error_message: error instanceof Error ? error.message : String(error),
				});
				return;
			}
			track("gaps_removal_success", { ...analyticsBase, reason: pending.reason, call_off_agent: pending.callOffFired });
			onRemoved({ groupKey: group.key, before: totalsBefore, callOffFired: pending.callOffFired, name, reason: pending.reason });
		},
		[analyticsBase, group.key, name, onRemoved, totalsBefore, track, trackError],
	);
	return { analyticsBase, pendingRef, onFinished };
};

/**
 * The removal behind "Why is this person off?" (P3-3): the desktop removal dialog's own state, single shift only (the
 * recurring modes are pinned off, so nothing here can reach a whole series), plus the one rule the classic mobile
 * screen had on top — a removal a week or more out asks twice (`REMOVE_NURSE_DAYS_THRESHOLD`) — and "Other" needing
 * words (`canConfirmRemoval`).
 */
export const useGapsRemoval = ({ onBack, ...params }: Params) => {
	const { track } = useAnalytics();
	const { isHospitalDemo } = useHospitalData();
	const [isFarOutConfirmOpen, setIsFarOutConfirmOpen] = useState<boolean>(false);
	const { analyticsBase, pendingRef, onFinished } = useRemovalOutcome(params);
	const removal = useDeleteRecurringOrSingleState({
		nurseInShift: params.nurseInShift,
		onFinished,
		onCancel: onBack,
		source: ChangesSource.Staffing,
		showActual: false,
		allowRecurringModes: false,
	});
	const reasons: GapsRemovalReasons = useCallInReasonOptions({ enabled: !removal.dontRenderReasonSelector });
	const showsReasons = !removal.dontRenderReasonSelector && !removal.isSelfSchedulingShift;
	const isOther = isOtherReasonLabel(removal.selectedReason);
	const canConfirm = canConfirmRemoval({
		showsReasons,
		selectedReason: removal.selectedReason,
		isOther,
		removalMessage: removal.removalMessage,
		deleteValid: removal.deleteValid,
		isLoading: removal.isLoading,
	});
	const daysOut = removalDaysOut(params.nurseInShift.startTime, DateTime.now());

	const runRemoval = useCallback(async (): Promise<void> => {
		// The hook's own gate for the call-off agent (`sendCallOffShift` in `handleDeleteSingle`): the checkbox, a
		// ready gate, a live department. Draft mode — the hook's other condition — already hides the checkbox, and it
		// is unreachable on mobile.
		const callOffFired = removal.shouldFindReplacementForShift && removal.isReplacementGateReady && !isHospitalDemo;
		// DEFAULT: a self-scheduling or hospital removal has no picked reason; the hook records its own for those.
		const reason = removal.selectedReason ?? "";
		pendingRef.current = { callOffFired, reason };
		track("gaps_removal_start", { ...analyticsBase, reason, call_off_agent: callOffFired, days_out: daysOut });
		await removal.handleConfirm();
	}, [removal, isHospitalDemo, pendingRef, track, analyticsBase, daysOut]);

	const confirm = useCallback((): void => {
		if (!canConfirm) return;
		if (needsFarOutConfirm(daysOut, REMOVE_NURSE_DAYS_THRESHOLD)) {
			track("gaps_removal_far_out_confirm_shown", { ...analyticsBase, days_out: daysOut });
			setIsFarOutConfirmOpen(true);
			return;
		}
		void runRemoval();
	}, [canConfirm, daysOut, runRemoval, track, analyticsBase]);

	const pickReason = useCallback(
		(reason: string): void => {
			track("gaps_removal_reason_selected", { ...analyticsBase, reason });
			removal.handleReasonChange(reason);
		},
		[analyticsBase, removal, track],
	);

	return { removal, reasons, showsReasons, isOther, canConfirm, daysOut, confirm, pickReason, runRemoval, isFarOutConfirmOpen, setIsFarOutConfirmOpen };
};
